import {
  bidAgeLabel,
  bidHistoryLabel,
  conditionLabel,
  LIVE_ROOM_LAYOUTS,
  type AuctionBidHistoryEntry,
  type LiveRoomLayoutId,
} from '@auctisync/api-client';
import { formatCents } from '@auctisync/shared';
import { useState, type ReactElement } from 'react';
import type { LiveLot } from '@auctisync/api-client';

import { SpecsTable } from './SpecsTable';

/**
 * The Live Room's ten layouts, on the web.
 *
 * Jim, 2026-09-14: *"the 10 views must be on the website."*
 *
 * The website offered three — Stage, Grid, Table — which is exactly what
 * `docs/design-handoff/bidder/web/web-live.jsx` specifies. The TEN are the
 * catalogue in `LIVE_ROOM_LAYOUTS` (@auctisync/api-client), shipped on the app
 * on Jim's 2026-08-05 instruction: *"The intention was to give the users options
 * for their view."*
 *
 * 🔴 PORTED, NOT INVENTED. The desktop form of every one of these is specified
 * in `docs/design-handoff/ui_kits/bidder/` — `live-room-desktop.jsx` (A-E),
 * `live-room-desktop-2.jsx` (F-J), with the rules in `live-room-shared.jsx`
 * (now in `bidder-web.css` under the same `lr-*` names). The class names here
 * are that stylesheet's, so the visual is the design's rather than mine.
 *
 * 🔴 EVERY LAYOUT IS A PURE FUNCTION OF `LiveLayoutProps` — the same constraint
 * the app's `live-room/layouts.tsx` holds itself to. No layout fetches, no
 * layout owns state, and none of them knows which auction it is rendering. That
 * is what lets the switcher swap one for another without the page re-reading
 * anything, and what keeps a tenth layout from becoming a tenth data path.
 *
 * ⚠️ THE APP AND THE WEBSITE DO NOT SHARE THESE COMPONENTS, and should not try
 * to: one is React Native, one is DOM. What they DO share is the catalogue of
 * ids and labels, and the stored preference. Jim, 2026-09-14, on the two
 * surfaces differing: *"There will have to be some differences."* The rule that
 * binds is that a bidder is never told two different things about the same fact
 * — so the DATA below all comes from the page, which computed it once.
 */

/**
 * The row every layout renders a lot from — the SHARED `LiveLot`
 * (`@auctisync/api-client`, `toLiveLot`), the same one the app renders.
 *
 * 🔴 IT WAS A COPY. This file declared its own `LiveLot` and `PublicLiveRoom`
 * mapped catalogue rows into it inline, while the app mapped them in
 * `toLiveLot.ts`: two answers to "what is this lot's name, its price, has it
 * ended" that had drifted on six questions (2026-10-08). One type, one mapping
 * now — the engine's live view laid over the catalogue row.
 *
 * Two of its fields every layout must read before printing a figure:
 * `priceHidden` and `bidsHidden` — the house hides that figure from this
 * viewer (memory: reference_auction_visibility_flags). `priceText` and
 * `bidsCount` below are how a layout prints them. `secs` is `Infinity` for a
 * lot with no close time: `fmtSecs` prints a dash.
 *
 * `specs` is ALWAYS NULL from the live room, and deliberately so: specs live
 * on `inventory_items.custom_fields`, which the lot page fetches in a second
 * query. Carrying them here would mean joining inventory for every lot on the
 * page; `SpecsTable` is shared so that when they do arrive there is still only
 * one table.
 */
export type { LiveLot } from '@auctisync/api-client';

export interface LiveLayoutProps {
  /** The lots on THIS PAGE, in run order. Most layouts want only this. */
  lots: LiveLot[];
  /**
   * The filtered run, UNPAGINATED.
   *
   * 🔴 A WATCHLIST IS A CLAIM ABOUT THE BIDDER, NOT ABOUT THE PAGE. Fed only
   * the current slice, Watchlist Rail said "Watching · 0 — nothing watched yet"
   * on page 2 while the standing panel above it said Watching 2. One of them
   * was lying. Same rule `PublicLiveRoom` already states for the standing
   * counts: they read the whole sale, never the filtered view.
   *
   * Split Compare draws its pickers from this for the same reason — a bidder
   * comparing lot 3 against lot 180 should not have to paginate to do it.
   */
  allLots: LiveLot[];
  /** The recent bid feed, for the layouts that show activity. */
  events: AuctionBidHistoryEntry[];
  /** Server clock reader, so a countdown cannot drift off the bidder's clock. */
  nowServerMs: () => number;
  /** The connection is down, so nothing here is claiming to be current. */
  offline: boolean;
  /** Open a lot's page. */
  onOpenLot: (lot: LiveLot) => void;
  /**
   * Go to the lot's page with the intent to bid — the page focuses its maximum
   * field. A SHORTCUT, not a second bid form: the increment ladder, the
   * secret-maximum rule, the rejection paths and the live standing all live on
   * the lot page, and a copy in here would be a second money path.
   * Only Focused Watch reads it today.
   */
  onBidLot: (lot: LiveLot) => void;
  /** Which lots this bidder watches. Empty when signed out. */
  watchedIds: Set<string>;
  /** Null when signed out — the layouts then draw no watch control at all. */
  onToggleWatch: ((lotId: string) => void) | null;
}

/**
 * `mm:ss` under an hour, `h:mm:ss` over it, and a word once it is done.
 *
 * Lives here rather than in the page because ten layouts need it and the page
 * needed one copy; the page imports this one.
 */
export function fmtSecs(s: number): string {
  // No close time (`secondsUntil` gives Infinity): not ended, not a number.
  if (!Number.isFinite(s)) return s > 0 ? '—' : 'ended';
  if (s <= 0) return 'ended';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${String(h)}:${two(m)}:${two(sec)}` : `${two(m)}:${two(sec)}`;
}

/**
 * The urgency class the stylesheet keys its borders and pulses off.
 *
 * ⚠️ THESE ARE THE LOCKED AUCTION SEMANTICS. Red is closing, amber is live.
 * The thresholds live here once so ten layouts cannot disagree about when a lot
 * becomes urgent — a bidder switching view must not see the same lot calm in
 * one and alarming in another.
 */
export function secTone(s: number): '' | 'urgent' | 'closing' {
  if (s <= 0) return '';
  if (s < 60) return 'closing';
  if (s < 600) return 'urgent';
  return '';
}

/**
 * The price as a layout prints it. A price the house hides from this viewer
 * is a dash — never the opening bid, which `current` holds only so older
 * renderers kept a number (memory: reference_auction_visibility_flags).
 */
export function priceText(lot: Pick<LiveLot, 'current' | 'priceHidden'>): string {
  return lot.priceHidden ? '—' : formatCents(lot.current);
}

/** The bid count as a cell prints it: a dash when the house hides it. */
export function bidsCount(lot: Pick<LiveLot, 'bids' | 'bidsHidden'>): string {
  return lot.bidsHidden ? '—' : String(lot.bids);
}

/** "3 bids", or nothing at all when the house hides the count. */
export function bidsText(lot: Pick<LiveLot, 'bids' | 'bidsHidden'>): string | null {
  if (lot.bidsHidden) return null;
  return `${String(lot.bids)} ${lot.bids === 1 ? 'bid' : 'bids'}`;
}

/**
 * Soonest first, for the layouts that run in close order. `Infinity` (no
 * close time) sorts last, and two of them are equal — `a - b` would be NaN.
 */
function bySecs(a: Pick<LiveLot, 'secs'>, b: Pick<LiveLot, 'secs'>): number {
  return a.secs === b.secs ? 0 : a.secs < b.secs ? -1 : 1;
}

/** A lot photograph, or the stylesheet's own placeholder when there is none. */
function Photo({ lot, className }: { lot: LiveLot; className: string }) {
  return (
    <div className={className}>
      {lot.photo !== null ? (
        <img
          src={lot.photo}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span>Lot {lot.lotNumber}</span>
      )}
    </div>
  );
}

/** "You win" / "Outbid". Absent when the bidder has no standing on the lot. */
function YouPill({ you }: { you: 'winning' | 'outbid' | null }) {
  if (you === null) return null;
  return <span className={`lr-you ${you}`}>{you === 'winning' ? 'YOU WIN' : 'OUTBID'}</span>;
}

/**
 * 🔴 THE CARD **IS** THE BUTTON. A div with onClick is not a control — and
 * neither is a button buried inside one.
 *
 * It used to render `<article className={card}><button style={{all:'unset'}}>`.
 * `.lr-sidecard` is `display: grid; grid-template-columns: 52px 1fr`, so that
 * inner button was the single item in the FIRST track — 52px wide, the
 * thumbnail — and the card's own grid never saw the thumb and body as separate
 * children. Everything else on the card was dead space, and `width: 100%` could
 * not rescue it: the track is 52px. Same shape on `.lr-col-card` and
 * `.lr-watchcard`. It looked fine on `.lr-upcard` only because that class is a
 * plain block. In Focused Watch this meant clicking a queue card's title did
 * nothing at all.
 *
 * The card class moves onto the button and `data-lot-card` picks up the UA
 * reset from the stylesheet — deliberately unprefixed there, so it loses to
 * every card class and a card keeps its own display, padding and width. The
 * inline `all: unset` is gone; it was what forced the reset inline in the first
 * place, and it fought the card classes it sat inside.
 *
 * ⚠️ ONE CARD = ONE BUTTON. Do not put a second interactive element inside
 * these: nested interactives are invalid HTML and break Enter. A card needing a
 * second action stops being a button and gets an explicit one.
 *
 * `actionLabel` is the VERB the screen reader hears — "Open" everywhere except
 * Focused Watch's rail, which selects the hero rather than leaving the page.
 * Announcing "Open lot 8" for a control that does not open anything is the kind
 * of mismatch only a screen-reader user ever hits, and it costs one prop.
 */
function OpenLot({
  lot,
  onOpenLot,
  className,
  actionLabel = 'Open',
  children,
}: {
  lot: LiveLot;
  onOpenLot: (lot: LiveLot) => void;
  className: string;
  actionLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-lot-card
      data-lot-id={lot.id}
      className={className}
      onClick={() => {
        onOpenLot(lot);
      }}
      aria-label={`${actionLabel} lot ${String(lot.lotNumber)}: ${lot.title}`}
    >
      {children}
    </button>
  );
}

/** The shared "upcoming lots" grid that layouts A and B both end with. */
function UpcomingGrid({
  lots,
  onOpenLot,
  heading,
}: {
  lots: LiveLot[];
  onOpenLot: (lot: LiveLot) => void;
  heading: string;
}) {
  return (
    <section className="lr-upcoming">
      <div className="lr-upcoming-h">
        <h2>{heading}</h2>
        <span>
          {lots.length} {lots.length === 1 ? 'lot' : 'lots'}
        </span>
      </div>
      <div className="lr-upgrid">
        {lots.map((lot) => (
          <OpenLot key={lot.id} lot={lot} onOpenLot={onOpenLot} className="lr-upcard">
            <Photo lot={lot} className="lr-upcard-photo" />
            <div className="lr-upcard-body">
              <h3 className="lr-upcard-title">{lot.title}</h3>
              <span className="lr-upcard-bid">{priceText(lot)}</span>
              <span className="lr-upcard-time">{fmtSecs(lot.secs)}</span>
            </div>
          </OpenLot>
        ))}
      </div>
    </section>
  );
}

/* ─── The catalogue · "All lots" — WEB ONLY ─────────────────────────────── */

/**
 * The plain catalogue: every lot in the sale, in the order the house catalogued
 * it, on one grid.
 *
 * Jim, 2026-09-15: *"there is no view that just shows all of the listings in
 * order. That should be the default view."* He was right, and it was a hole
 * rather than a preference — all ten existing layouts re-shape the sale somehow
 * (by closing time, by what you watch, by photograph, by one hero lot). The
 * incumbent DTA site opens on exactly this.
 *
 * `UpcomingGrid` already existed but was only ever the lower half of Reel,
 * Ticker and Watchlist Rail; it was never reachable on its own.
 *
 * 🔴 IT IMPOSES NO ORDER OF ITS OWN. Every other layout re-sorts by time; this
 * one renders the run exactly as the page hands it over, so the sort control in
 * the toolbar actually does something on the default view. The page's default
 * sort is `lot_number`, which is what makes that order the catalogue order —
 * a bidder who picks "Closing soonest" gets closing soonest here too.
 *
 * ⚠️ WEB ONLY. It is deliberately NOT in `LIVE_ROOM_LAYOUTS` in
 * `@auctisync/api-client`; that shared list stays at ten and the native build
 * is untouched. A catalogue is a wide-grid, many-lots-at-once view whose whole
 * argument is how much is on screen, and the phone has one column.
 */
export function Catalogue({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  return (
    <div data-testid="live-layout-catalogue">
      <UpcomingGrid lots={lots} onOpenLot={onOpenLot} heading="All lots" />
    </div>
  );
}

/* ─── Pagination ────────────────────────────────────────────────────────── */

/** The page-size options. 10 is the default — Jim, 2026-09-15. */
export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 10;
/** Device-local, because it is a function of the SCREEN, not of the sale. */
export const PAGE_SIZE_STORE_KEY = 'bidder-lots-per-page';

/**
 * Which page numbers to print: first, last, and the current ±1, with gaps.
 *
 * A 40-page sale must not print forty controls. Pure so the windowing can be
 * tested at its edges — the interesting cases are all near 1 and near `total`,
 * where a naive window runs off the end and prints fewer than it should.
 */
export function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 1) return total === 1 ? [1] : [];
  const keep = new Set<number>([1, total, current - 1, current, current + 1]);
  const pages = [...keep].filter((n) => n >= 1 && n <= total).sort((x, y) => x - y);
  const out: (number | 'gap')[] = [];
  let prev = 0;
  for (const n of pages) {
    if (prev !== 0) {
      /**
       * 🔴 A GAP STANDING FOR EXACTLY ONE PAGE IS WORSE THAN THE PAGE. It takes
       * the same room, and it hides a control the bidder could otherwise click.
       * On a four-page sale sitting on page 1 the window was `1 2 … 4` — the
       * ellipsis concealing page 3 and nothing else, so reaching it took two
       * clicks instead of one. Print the number instead whenever the gap would
       * cover a single page.
       */
      if (n - prev === 2) out.push(prev + 1);
      else if (n - prev > 2) out.push('gap');
    }
    out.push(n);
    prev = n;
  }
  return out;
}

/**
 * Which page to land on when the page SIZE changes.
 *
 * 🔴 HOLD THE FIRST VISIBLE LOT, NOT THE PAGE NUMBER. At 100 per page, "page 4"
 * is a completely different part of the sale than it was at 10 — keeping the
 * number teleports the bidder somewhere they did not ask to go. Anchoring on
 * the first lot they can currently see keeps their place.
 *
 * Pure and separate because the sale this was built against has six open lots,
 * so multi-page behaviour cannot be exercised in a browser against real data.
 * The arithmetic is the part that can be wrong, so the arithmetic is tested.
 */
export function pageAfterSizeChange(page: number, oldSize: number, newSize: number): number {
  const firstVisibleIndex = Math.max(0, (page - 1) * oldSize);
  return Math.floor(firstVisibleIndex / newSize) + 1;
}

/**
 * The pager row. Rendered by the PAGE, once, above and below the lots.
 *
 * 🔴 EVERY CONTROL IS A REAL `<button type="button">`. It was first written as
 * bare `<a>` with no `href`, which takes no focus and no keyboard — a bidder on
 * a keyboard could not leave page 1. The same rule `OpenLot` states a few
 * hundred lines up: a div with onClick is not a control.
 *
 * A page that cannot be gone to renders as a disabled button rather than
 * disappearing, so the row keeps its width at the ends instead of the controls
 * shifting under the cursor.
 *
 * The classes are the stylesheet's EXISTING `.lr-pager` family — written for a
 * pager that was never built. A parallel set was authored during design without
 * grepping first, collided on rule order, and was thrown away.
 */
export function Pager({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
  idSuffix,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
  onPageSize: (n: number) => void;
  idSuffix: string;
}): ReactElement | null {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return (
    <div className="lr-pager">
      <label>
        <span className="w-lbl" style={{ marginRight: 6 }}>
          Per page
        </span>
        <select
          value={pageSize}
          aria-label="Lots per page"
          onChange={(e) => {
            onPageSize(Number(e.target.value));
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      <span className="lr-reel-bids-count">
        {first}–{last} of {total}
      </span>

      <div className="lr-pager-nums">
        <button
          type="button"
          onClick={() => {
            onPage(page - 1);
          }}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>
        {pageWindow(page, pageCount).map((n, i) =>
          n === 'gap' ? (
            <span key={`gap-${idSuffix}-${String(i)}`} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={`p-${idSuffix}-${String(n)}`}
              type="button"
              className={n === page ? 'on' : undefined}
              aria-label={`Page ${String(n)}`}
              {...(n === page ? { 'aria-current': 'page' as const } : {})}
              onClick={() => {
                onPage(n);
              }}
            >
              {n}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => {
            onPage(page + 1);
          }}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </div>
  );
}

/* ─── A · Closing Reel ──────────────────────────────────────────────────── */

/** A horizontal run of the soonest to close, then everything else beneath. */
export function ClosingReel({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  /**
   * 🔴 HALF THE PAGE, CAPPED AT 8 — not a hard 8.
   *
   * The page size is the bidder's now, and the default is 10. A fixed reel of
   * eight left two cards in the grid underneath, which reads as a bug rather
   * than a layout. Half keeps both halves recognisable at every page size, and
   * the cap stops the reel running off the side on a 100-lot page.
   */
  const reel = lots.slice(0, Math.min(8, Math.ceil(lots.length / 2)));
  const rest = lots.slice(reel.length);
  return (
    <div data-testid="live-layout-reel">
      <div className="lr-upcoming-h" style={{ padding: '18px 0 10px' }}>
        <h2>Closing next</h2>
        <span>scroll for more →</span>
      </div>
      <div className="lr-reel">
        {reel.map((lot) => (
          <OpenLot
            key={lot.id}
            lot={lot}
            onOpenLot={onOpenLot}
            className={`lr-reel-card ${secTone(lot.secs)}`}
          >
            <div style={{ position: 'relative' }}>
              <Photo lot={lot} className="lr-reel-photo" />
              <div className="lr-reel-countdown">{fmtSecs(lot.secs)}</div>
              {lot.you !== null ? (
                <div style={{ position: 'absolute', top: 10, left: 10 }}>
                  <YouPill you={lot.you} />
                </div>
              ) : null}
            </div>
            <div className="lr-reel-body">
              <h3 className="lr-reel-title">{lot.title}</h3>
              <div className="lr-reel-meta">
                <span className="lr-reel-bid">{priceText(lot)}</span>
                <span className="lr-reel-bids-count">{bidsText(lot)}</span>
              </div>
            </div>
          </OpenLot>
        ))}
      </div>
      {rest.length > 0 ? (
        <UpcomingGrid lots={rest} onOpenLot={onOpenLot} heading="Upcoming lots" />
      ) : null}
    </div>
  );
}

/* ─── B · Ticker + Grid ─────────────────────────────────────────────────── */

/**
 * A live activity strip over the grid.
 *
 * 🔴 A NULL AMOUNT IS A DASH, NEVER A NUMBER. `amountCents` comes back null when
 * the house hides winning information and the bid is not the caller's own —
 * printing anything numeric there would leak exactly what the house suppressed.
 * The same rule the activity feed already follows.
 */
export function TickerGrid({
  lots,
  events,
  nowServerMs,
  offline,
  onOpenLot,
}: LiveLayoutProps): ReactElement {
  return (
    <div data-testid="live-layout-ticker">
      <div className="lr-ticker">
        <div className="lr-ticker-label">{offline ? 'PAUSED' : 'LIVE'}</div>
        <div className="lr-ticker-track">
          {events.length === 0 ? (
            <span className="lr-ticker-item">
              <span className="who">No bids yet</span>
            </span>
          ) : (
            events.slice(0, 12).map((e) => {
              const you = bidHistoryLabel(e) === 'you';
              return (
                <span className="lr-ticker-item" key={e.id}>
                  <span className={`who${you ? 'you' : ''}`}>{you ? 'You' : 'bidder'}</span>
                  <span className="sep">·</span>
                  <span>Lot {e.lotNumber}</span>
                  <span className="amt">
                    {e.amountCents === null ? '—' : formatCents(e.amountCents)}
                  </span>
                  <span className="sep">{bidAgeLabel(e.createdAt, nowServerMs())}</span>
                </span>
              );
            })
          )}
        </div>
      </div>
      <UpcomingGrid lots={lots} onOpenLot={onOpenLot} heading="Open lots" />
    </div>
  );
}

/* ─── C · Focused Watch ─────────────────────────────────────────────────── */

/**
 * One hero lot, with the queue in a rail beside it.
 *
 * 🔴 THE RAIL SELECTS, IT DOES NOT NAVIGATE (Jim, 2026-09-14): *"In Focused
 * Watch view, I should be able to click on the lots in the right column and it
 * should open the lot in the main part of the page."*
 *
 * Every card in every other layout leaves for the lot page, because that is
 * where bidding lives. This one is different on purpose: the whole point of a
 * hero-plus-queue layout is comparing one lot against the next WITHOUT losing
 * the room, and a rail that navigated away made the main pane unreachable — the
 * hero was always `lots[0]` and nothing could change it.
 *
 * The hero's own button still leaves for the lot page. That is the only bid
 * path, and this layout does not grow a second one.
 *
 * Selection is BY ID, resolved against the current list on every render rather
 * than held as an index. Lots close and drop out of an open-lots list that is
 * re-sorted every second; an index would silently come to mean a different lot,
 * and a stale id falls back to the front of the queue instead of an empty pane.
 */
// `onOpenLot` is deliberately NOT destructured: round 2 replaced this layout's
// "Open lot" button with the Bid CTA, and the queue cards select the hero
// rather than navigating. The only route out of this view is the CTA.
export function FocusedWatch({ lots, onBidLot }: LiveLayoutProps): ReactElement {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const hero = lots.find((l) => l.id === selectedId) ?? lots[0];
  const queue = lots.filter((l) => l.id !== hero?.id);
  if (hero === undefined) return <div data-testid="live-layout-focused" />;
  return (
    <div className="lr-focus" data-testid="live-layout-focused">
      <div className="lr-focus-main" data-lot-id={hero.id}>
        <div style={{ position: 'relative' }}>
          <Photo lot={hero} className="lr-focus-hero" />
          {hero.you !== null ? (
            <div style={{ position: 'absolute', top: 14, left: 14 }}>
              <YouPill you={hero.you} />
            </div>
          ) : null}
        </div>
        <h2 style={{ fontSize: 22, margin: '16px 0 6px' }}>{hero.title}</h2>
        <div style={{ display: 'flex', gap: 18, alignItems: 'baseline' }}>
          <span className="lr-reel-bid" style={{ fontSize: 26 }}>
            {priceText(hero)}
          </span>
          <span className="lr-reel-bids-count">{bidsText(hero)}</span>
          <span className="lr-reel-bids-count">closes {fmtSecs(hero.secs)}</span>
        </div>
        {/* 🔴 THE LOT'S DETAILS LIVE UNDER THE HERO (Jim, 2026-09-15: *"a ton
            of white space"*). The main column was empty below the price while
            the rail ran 5,607px tall beside it. The cure is both: the queue
            scrolls inside itself (see the stylesheet) AND this column has
            something to say. */}
        <button
          type="button"
          className="w-btn w-btn-cta w-btn-lg"
          style={{ marginTop: 16 }}
          onClick={() => {
            onBidLot(hero);
          }}
        >
          Bid on lot {hero.lotNumber}
        </button>

        <div className="w-fact" style={{ marginTop: 20 }}>
          <div className="w-kv">
            <span className="muted">Bids</span>
            <span>{bidsCount(hero)}</span>
          </div>
          <div className="w-kv">
            <span className="muted">Opened at</span>
            <span>{formatCents(hero.openedAt)}</span>
          </div>
          <div className="w-kv">
            <span className="muted">Reserve</span>
            <span>
              {hero.hasReserve === false
                ? 'None'
                : hero.hasReserve === true
                  ? 'Yes'
                  : /* null is UNKNOWN, not "no reserve" — the house never
                       recorded one, and guessing is a claim about the terms. */
                    '—'}
            </span>
          </div>
          <div className="w-kv">
            <span className="muted">Closes</span>
            <span>{fmtSecs(hero.secs)}</span>
          </div>
        </div>

        {conditionLabel(hero.condition) ? (
          <section className="w-sec" style={{ paddingTop: 22 }}>
            <h2 className="w-h2" style={{ fontSize: 18, marginBottom: 10 }}>
              Condition
            </h2>
            {/* The label, not the code — `conditionLabel`, shared with the app. */}
            <p className="w-body">{conditionLabel(hero.condition)}</p>
          </section>
        ) : null}

        {/* The SHARED table — the lot page renders the same component. */}
        {hero.specs !== null ? <SpecsTable specs={hero.specs} headingSize={18} /> : null}
      </div>
      <div className="lr-focus-rail">
        <h3 className="lr-focus-rail-h">Up next</h3>
        {/* 🔴 THE QUEUE SCROLLS, NOT THE RAIL. The heading stays a direct child
            so it does not scroll away; the cards get their own bounded box. */}
        <div className="lr-focus-queue">
          {queue.map((lot) => (
            <OpenLot
              key={lot.id}
              lot={lot}
              onOpenLot={(l) => {
                setSelectedId(l.id);
              }}
              actionLabel="Show"
              className="lr-sidecard"
            >
              <Photo lot={lot} className="lr-sidecard-thumb" />
              <div className="lr-sidecard-body">
                <div className="lr-sidecard-title">{lot.title}</div>
                <div className="lr-sidecard-row">
                  <span className="lr-sidecard-bid">{priceText(lot)}</span>
                  <span className="lr-reel-bids-count">{fmtSecs(lot.secs)}</span>
                </div>
              </div>
            </OpenLot>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── D · Sortable Table ────────────────────────────────────────────────── */

/** The dense one. Every lot, one row each, sorted by whatever is clicked. */
export function SortableTable({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  return (
    <div className="lr-table-wrap" data-testid="live-layout-sortable">
      <div className="lr-table">
        <div className="lr-table-row header">
          <span>Lot</span>
          <span>Item</span>
          <span>Bids</span>
          <span>Current</span>
          <span>You</span>
          <span>Reserve</span>
          <span />
          <span>Closes</span>
        </div>
        {lots.map((lot) => (
          <div className="lr-table-row" key={lot.id} data-lot-id={lot.id}>
            <Photo lot={lot} className="lr-table-thumb" />
            <div style={{ minWidth: 0 }}>
              <div className="lr-table-title">{lot.title}</div>
              <div className="lr-table-title-sub">Lot {lot.lotNumber}</div>
            </div>
            <span>{bidsCount(lot)}</span>
            <span className="lr-table-bid">{priceText(lot)}</span>
            <span>
              <YouPill you={lot.you} />
            </span>
            <span className="lr-reel-bids-count">
              {lot.hasReserve === false ? 'None' : lot.hasReserve === true ? 'Yes' : '—'}
            </span>
            <span />
            <button
              type="button"
              className="w-btn w-btn-out w-btn-sm"
              onClick={() => {
                onOpenLot(lot);
              }}
            >
              {fmtSecs(lot.secs)}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── E · Watchlist Rail ────────────────────────────────────────────────── */

/**
 * The lots this bidder is watching, large, with everything else beside them.
 *
 * Signed out there is no watchlist, so this says so rather than rendering an
 * empty rail that looks broken.
 */
export function WatchlistRail({
  lots,
  allLots,
  watchedIds,
  onToggleWatch,
  onOpenLot,
}: LiveLayoutProps): ReactElement {
  /**
   * 🔴 THE RAIL READS THE WHOLE RUN; THE GRID READS THE PAGE.
   *
   * Fed only the page, the rail showed "Watching · 0 — nothing watched yet" on
   * page 2 while the standing panel directly above it said Watching 2. A
   * watchlist is a claim about the BIDDER, not about which page they happen to
   * be on, and the count beside the heading has to match the one above it.
   */
  const watched = allLots.filter((l) => watchedIds.has(l.id));
  const rest = lots.filter((l) => !watchedIds.has(l.id));
  return (
    <div className="lr-focus" data-testid="live-layout-watchlist">
      <div className="lr-focus-main">
        <UpcomingGrid lots={rest} onOpenLot={onOpenLot} heading="Every open lot" />
      </div>
      <div className="lr-focus-rail">
        <h3 className="lr-focus-rail-h">
          {onToggleWatch === null ? 'Watching' : `Watching · ${String(watched.length)}`}
        </h3>
        {onToggleWatch === null ? (
          <p className="lr-reel-bids-count">Sign in to keep a watchlist.</p>
        ) : watched.length === 0 ? (
          <p className="lr-reel-bids-count">
            Nothing watched yet. The heart on any lot adds it here.
          </p>
        ) : (
          watched.map((lot) => (
            <OpenLot key={lot.id} lot={lot} onOpenLot={onOpenLot} className="lr-watchcard">
              <Photo lot={lot} className="lr-watchcard-photo" />
              <div className="lr-watchcard-body">
                <h4 className="lr-watchcard-title">{lot.title}</h4>
                <div className="lr-watchcard-row">
                  <span className="lr-watchcard-bid">{priceText(lot)}</span>
                  <span className="lr-reel-bids-count">{fmtSecs(lot.secs)}</span>
                </div>
              </div>
            </OpenLot>
          ))
        )}
      </div>
    </div>
  );
}

/* ─── F · Split Compare ─────────────────────────────────────────────────── */

/**
 * Two lots, figure by figure — and the bidder chooses WHICH two.
 *
 * 🔴 SELECTION IS HELD BY ID AND RESOLVED EACH RENDER, never by index. The run
 * is re-sorted every second as lots close and drop out; an index would silently
 * come to mean a different lot mid-comparison, which on a page about numbers is
 * the worst place for it. A lot that closes falls out of `allLots`, the id stops
 * resolving, and the pane falls back to the first still-open lot rather than
 * emptying.
 *
 * The options come from `allLots`, not the page: comparing lot 3 against lot
 * 180 should not require paginating to one of them first.
 */
export function SplitCompare({ lots, allLots, onOpenLot }: LiveLayoutProps): ReactElement {
  const pool = allLots.length > 0 ? allLots : lots;
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);

  const a = pool.find((l) => l.id === leftId) ?? pool[0];
  const b = pool.find((l) => l.id === rightId) ?? pool.find((l) => l.id !== a?.id) ?? pool[1];

  if (a === undefined || b === undefined) {
    return (
      <div className="lr-split-pick" data-testid="live-layout-split">
        Two open lots are needed to compare.
      </div>
    );
  }
  const rows: [string, string, string][] = [
    ['Current', priceText(a), priceText(b)],
    ['Bids', bidsCount(a), bidsCount(b)],
    ['Closes', fmtSecs(a.secs), fmtSecs(b.secs)],
    [
      'Reserve',
      a.hasReserve === false ? 'None' : a.hasReserve === true ? 'Yes' : '—',
      b.hasReserve === false ? 'None' : b.hasReserve === true ? 'Yes' : '—',
    ],
  ];
  return (
    <div data-testid="live-layout-split">
      <div className="lr-split">
        {(
          [
            [a, leftId, setLeftId, 'Left'],
            [b, rightId, setRightId, 'Right'],
          ] as const
        ).map(([lot, sel, setSel, side]) => (
          <div className="lr-split-pane" key={side} data-lot-id={lot.id}>
            <div className="lr-split-pick">
              <select
                value={sel ?? lot.id}
                aria-label={`${side} lot to compare`}
                onChange={(e) => {
                  setSel(e.target.value);
                }}
              >
                {pool.map((o) => (
                  <option key={o.id} value={o.id}>
                    Lot {o.lotNumber} · {o.title}
                  </option>
                ))}
              </select>
            </div>
            <Photo lot={lot} className="lr-split-hero" />
            <h3 style={{ margin: '14px 0 4px' }}>{lot.title}</h3>
            <div className="lr-reel-bids-count">Lot {lot.lotNumber}</div>
            <button
              type="button"
              className="w-btn w-btn-out w-btn-sm"
              style={{ marginTop: 12 }}
              onClick={() => {
                onOpenLot(lot);
              }}
            >
              Open this lot
            </button>
          </div>
        ))}
      </div>
      {rows.map(([k, left, right]) => (
        <div className="lr-compare-row" key={k}>
          <span style={{ textAlign: 'right' }}>{left}</span>
          <span className="k">{k}</span>
          <span>{right}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── G · Schedule Timeline ─────────────────────────────────────────────── */

const BUCKETS: [string, number][] = [
  ['Closing now', 60],
  ['Within the hour', 3600],
  ['Today', 86400],
  ['Later', Number.POSITIVE_INFINITY],
];

/**
 * Whether a lot belongs under a bucket's ceiling. The last bucket ("Later")
 * takes everything left — including a lot with no close time (`Infinity`),
 * which `secs < Infinity` would drop from the layout altogether.
 */
function inBucketFor(secs: number, ceiling: number): boolean {
  return ceiling === Number.POSITIVE_INFINITY || secs < ceiling;
}

/** Lots grouped by how soon they close. */
export function ScheduleTimeline({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  let remaining = [...lots].sort(bySecs);
  return (
    <div className="lr-timeline" data-testid="live-layout-schedule">
      {BUCKETS.map(([label, ceiling]) => {
        const inBucket = remaining.filter((l) => inBucketFor(l.secs, ceiling));
        remaining = remaining.filter((l) => !inBucketFor(l.secs, ceiling));
        if (inBucket.length === 0) return null;
        return (
          <div className="lr-tl-bucket" key={label}>
            <div className="lr-tl-bucket-h">
              <span className="lr-tl-bucket-label">{label}</span>
              <span className="lr-tl-bucket-line" />
              <span className="lr-tl-bucket-count">{inBucket.length}</span>
            </div>
            {inBucket.map((lot) => (
              <div className="lr-tl-item" key={lot.id} data-lot-id={lot.id}>
                <span className="lr-tl-time">{fmtSecs(lot.secs)}</span>
                <Photo lot={lot} className="lr-tl-thumb" />
                <div style={{ minWidth: 0 }}>
                  <div className="lr-tl-title">{lot.title}</div>
                  <div className="lr-tl-sub">
                    Lot {lot.lotNumber}
                    {bidsText(lot) === null ? '' : ` · ${bidsText(lot) ?? ''}`}
                  </div>
                </div>
                <span className="lr-tl-bid">{priceText(lot)}</span>
                <button
                  type="button"
                  className="w-btn w-btn-out w-btn-sm"
                  onClick={() => {
                    onOpenLot(lot);
                  }}
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ─── H · Gallery Wall ──────────────────────────────────────────────────── */

/** The photographs, with the price over them. */
export function GalleryWall({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  return (
    <div className="lr-gallery" data-testid="live-layout-gallery">
      {lots.map((lot) => (
        <OpenLot key={lot.id} lot={lot} onOpenLot={onOpenLot} className="lr-gal-card">
          <Photo lot={lot} className="lr-gal-photo" />
          <div className="lr-gal-count">{fmtSecs(lot.secs)}</div>
          <div className="lr-gal-overlay">
            <h3 className="lr-gal-title">{lot.title}</h3>
            <div className="lr-gal-row">
              <span className="lr-gal-bid">{priceText(lot)}</span>
              <span>{bidsText(lot)}</span>
            </div>
          </div>
        </OpenLot>
      ))}
    </div>
  );
}

/* ─── I · Spotlight Theater ─────────────────────────────────────────────── */

/**
 * The broadcast view. DARK BY DESIGN.
 *
 * ⚠️ The one layout that does not follow the page's light/dark setting — the
 * design fixes it dark because it is a stage, and a stage is dark. Same
 * decision the app made for its own Spotlight.
 *
 * 🔴 BUT THE LOT ITSELF IS LIT (Jim, 2026-09-15: *"The Spotlight Theater lot
 * card background is wrong."*). Lot photographs in this system are cut-outs —
 * PNGs with an ALPHA CHANNEL and no background of their own. Every other layout
 * lands them on a pale card, so nobody had noticed; this is the only near-black
 * surface in the product, and a dark object on transparency over a near-black
 * stage is invisible. The demo's black wire shelving disappeared completely.
 *
 * The design's own note calls the screen a "full-bleed lot image", which assumed
 * a photograph carrying its own background. That assumption does not hold for
 * this catalogue, so the lot sits on a LIT PLATE inside the dark stage instead —
 * which is what a spotlight actually is, and works for a cut-out and a
 * full-frame photograph alike.
 */
export function SpotlightTheater({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  const lead = lots[0];
  if (lead === undefined) return <div data-testid="live-layout-spotlight" />;
  return (
    <div className="lr-theater" data-testid="live-layout-spotlight">
      <div className="lr-theater-stage" data-lot-id={lead.id}>
        <div className="lr-theater-screen">
          {/* 🔴 ITS OWN CLASS. This was `className="lr-theater-screen"` — the
              stage class applied to its own child, so the radial gradient, the
              centring grid and the right-hand border all rendered twice, nested.
              A port slip, not a design decision. */}
          <Photo lot={lead} className="lr-theater-plate" />
        </div>
        <div className="lr-theater-panel">
          <div className="lr-theater-kicker">
            <span className="lr-live-dot" style={{ display: 'inline-block', marginRight: 8 }} />
            Lot {lead.lotNumber} · closing {fmtSecs(lead.secs)}
          </div>
          <h2 className="lr-theater-title">{lead.title}</h2>
          <div className="lr-theater-bid">{priceText(lead)}</div>
          <div className="lr-theater-kicker">{bidsText(lead)}</div>
          <button
            type="button"
            className="w-btn"
            onClick={() => {
              onOpenLot(lead);
            }}
          >
            Open this lot
          </button>
        </div>
      </div>
      <div className="lr-theater-filmstrip">
        {lots.slice(1).map((lot) => (
          <OpenLot key={lot.id} lot={lot} onOpenLot={onOpenLot} className="lr-theater-frame">
            <Photo lot={lot} className="lr-theater-frame-photo" />
            <div className="lr-theater-frame-cap">
              <span>{lot.lotNumber}</span>
              <span>{fmtSecs(lot.secs)}</span>
            </div>
          </OpenLot>
        ))}
      </div>
    </div>
  );
}

/* ─── J · Category Columns ──────────────────────────────────────────────── */

/**
 * Kanban columns, bucketed by how soon each lot closes.
 *
 * The app's version pages horizontally; this is the desktop form the design
 * specifies. Buckets rather than true categories because a lot's category is
 * not on the Live Room's row — using closing windows keeps every column
 * populated and honest instead of inventing a taxonomy.
 */
export function CategoryColumns({ lots, onOpenLot }: LiveLayoutProps): ReactElement {
  let remaining = [...lots].sort(bySecs);
  const columns = BUCKETS.map(([label, ceiling]) => {
    const inCol = remaining.filter((l) => inBucketFor(l.secs, ceiling));
    remaining = remaining.filter((l) => !inBucketFor(l.secs, ceiling));
    return { label, lots: inCol };
  }).filter((c) => c.lots.length > 0);

  return (
    <div className="lr-cols" data-testid="live-layout-category">
      {columns.map((col) => (
        <div className="lr-col" key={col.label}>
          <div className="lr-col-h">
            <span className="lr-col-title">{col.label}</span>
            <span className="lr-col-count">{col.lots.length}</span>
          </div>
          <div className="lr-col-body">
            {col.lots.map((lot) => (
              <OpenLot
                key={lot.id}
                lot={lot}
                onOpenLot={onOpenLot}
                className={`lr-col-card ${secTone(lot.secs)}`}
              >
                <Photo lot={lot} className="lr-col-thumb" />
                <div style={{ minWidth: 0 }}>
                  <div className="lr-col-card-title">{lot.title}</div>
                  <div className="lr-col-card-row">
                    <span className="lr-col-card-bid">{priceText(lot)}</span>
                    <span className="lr-reel-bids-count">{fmtSecs(lot.secs)}</span>
                  </div>
                </div>
              </OpenLot>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The catalogue's ids to this file's renderers.
 *
 * 🔴 KEYED BY `LiveRoomLayoutId`, so TypeScript fails the build the day an
 * eleventh layout joins `LIVE_ROOM_LAYOUTS` and the website has not grown one.
 * That is the whole reason the map is typed rather than a loose lookup — the
 * app has the same guard for the same reason.
 */
/**
 * Which layouts PAGINATE.
 *
 * Jim, 2026-09-15: *"the user should be able to choose how many lots per page
 * and the default should be 10. This should be available in Closing Reel,
 * Ticker + Grid, Sortable Table, Watchlist Rail, Schedule Timeline, Gallery
 * Wall."* Plus the catalogue, which is the view the instruction is really
 * about.
 *
 * The three that do not are hero-shaped rather than list-shaped: Focused Watch
 * is one lot plus a queue, Split Compare is exactly two, Spotlight is one and a
 * filmstrip. A page size on any of them either means nothing or starves the
 * thing the layout exists to show.
 *
 * 🔴 CATEGORY COLUMNS PAGINATES (round 2). It was `false` here, flagged at the
 * time as the arguable one — a run of lots in columns is still a run of lots,
 * which is the table's own rule for what paginates. Round 2 settled it: without
 * a pager it rendered the ENTIRE run while Gallery Wall beside it showed ten
 * and a "1-10 of 64" count, so the same sale reported two different sizes
 * depending on which view you were in. Eight paginate; three do not, and all
 * three are hero-shaped.
 *
 * 🔴 KEYED BY `WebLiveLayoutId`, so the build fails the day a twelfth layout
 * joins and nobody has answered the question. This replaces
 * `LAYOUT_HAS_OWN_RAIL`, which is DELETED: the standing and activity panels
 * moved into the header band, so every layout gets them and no layout is ever
 * three columns deep. That map was imported only by web files — nothing under
 * `apps/bidder` or `packages/ui-native` ever read it — so there was no "keep it
 * for the phone" version to keep. A map encoding a rule the product no longer
 * follows reads as live to the next person.
 */
export const LAYOUT_PAGINATES: Readonly<Record<WebLiveLayoutId, boolean>> = {
  catalogue: true,
  reel: true,
  ticker: true,
  sortable: true,
  watchlist: true,
  schedule: true,
  gallery: true,
  category: true,
  focused: false,
  split: false,
  spotlight: false,
};

/**
 * The WEBSITE's layout ids: the shared ten, plus `catalogue`.
 *
 * 🔴 `catalogue` IS DECLARED HERE, NOT IN `@auctisync/api-client`. That shared
 * list is read by the phone, and the typed renderer maps fail the build when a
 * shared id has no renderer — so adding it there would have broken the native
 * build until the app grew a layout nobody wants. Jim, 2026-09-15: *"I don't
 * think it belongs on the mobile native apps."*
 *
 * The phone reading a stored `catalogue` is handled: `normalizeLayoutId`
 * validates against the READING surface's own list and returns null for
 * anything else, so the phone falls through to its own default rather than
 * rendering nothing. Verified before shipping — it is the one thing holding
 * this arrangement together.
 */
export type WebLiveLayoutId = LiveRoomLayoutId | 'catalogue';

/**
 * What the website's chip row renders: the catalogue FIRST, then the shared
 * ten in their own order. Catalogue leads because it is the website's default
 * and the first thing a bidder with no stored preference lands on.
 */
/**
 * 🔴 THE WEB RELABELS `sortable`, IT DOES NOT RENAME IT.
 *
 * "Sortable Grid" promised something no part of the view does: the toolbar's
 * sort select drives every layout — the page sorts before any layout sees the
 * lots — and this view's header cells are `<span>`s with no handler. What
 * actually distinguishes it is DENSITY: a row per lot with bids, current,
 * standing and reserve, and it is the only layout showing reserve per lot.
 *
 * The ID stays `sortable`. It is the persisted view choice, the key in the
 * renderer map and the key in `LAYOUT_PAGINATES`; renaming it would silently
 * discard stored preferences and miss the renderer.
 *
 * ⚠️ OVERRIDDEN HERE RATHER THAN IN `@auctisync/api-client`. That label is read
 * by the phone too, and this round changes nothing under `apps/bidder`.
 */
const WEB_LABEL_OVERRIDES: Partial<Record<WebLiveLayoutId, string>> = {
  sortable: 'Details Table',
};

export const WEB_LAYOUT_CHIPS: readonly { id: WebLiveLayoutId; label: string }[] = [
  { id: 'catalogue', label: 'All lots' },
  ...LIVE_ROOM_LAYOUTS.map((l) => ({
    id: l.id as WebLiveLayoutId,
    label: WEB_LABEL_OVERRIDES[l.id] ?? l.label,
  })),
];

/**
 * The website's own normalizer.
 *
 * 🔴 NOT the shared `normalizeLayoutId`, which knows only the ten — using it
 * here would throw away a bidder's `catalogue` choice on every reload, sending
 * them back to the default they had just moved away from. The shared one stays
 * exactly as it is: it is the PHONE's guard, and its narrowness is the feature.
 */
export function normalizeWebLayoutId(value: unknown): WebLiveLayoutId | null {
  return WEB_LAYOUT_CHIPS.find((l) => l.id === value)?.id ?? null;
}

export const WEB_LIVE_LAYOUTS = {
  catalogue: Catalogue,
  reel: ClosingReel,
  ticker: TickerGrid,
  focused: FocusedWatch,
  sortable: SortableTable,
  watchlist: WatchlistRail,
  split: SplitCompare,
  schedule: ScheduleTimeline,
  gallery: GalleryWall,
  spotlight: SpotlightTheater,
  category: CategoryColumns,
} as const;
